/**
 * useHostSearch - generic admin-portal "find a host" lookup.
 *
 * Backed by ``GET /api/hosts/search/`` which fuzzy-matches via
 * trigram similarity across address, ZIP, owner name, and vehicle
 * identifiers (VIN / hull-id / registration number). Tenant scope is
 * enforced server-side via the holdings join - admin users see hosts
 * that have at least one holding for their tenant.
 *
 * NOT to be confused with the Smarty Streets address autocomplete
 * (``/api/lookup/address/...``) which validates / normalises a
 * real-world postal address for NEW registration creation. This one
 * searches the platform's existing host records.
 */

import { useQuery } from '@tanstack/react-query'

import api from '@/admin/lib/api'
import type { Address } from '@/shared/lib/formatting'

export interface HostSearchResult {
  uuid: string
  kind: string
  display_name: string
  address: Address | null
  /**
   * Vehicle-shaped tenants surface VIN / hull-id /
   * registration_number here. Empty string for property hosts.
   */
  vehicle_id: string
  current_owner_name: string
  structure_count: number
  active_holding_count: number
  /** 0..1 trigram similarity score, higher = better match. */
  match_score: number
}

export interface HostSearchResponse {
  results: HostSearchResult[]
}

export type HostSearchFieldGroup = 'address' | 'zip' | 'owner' | 'vehicle_id'

export function useHostSearch(
  query: string,
  options?: { limit?: number; fields?: HostSearchFieldGroup[] },
) {
  const trimmed = query.trim()
  const enabled = trimmed.length >= 2
  return useQuery<HostSearchResponse>({
    queryKey: [
      'host-search',
      trimmed,
      options?.limit ?? 10,
      options?.fields?.slice().sort().join(',') ?? '',
    ],
    queryFn: () =>
      api
        .get('/hosts/search/', {
          params: {
            q: trimmed,
            limit: options?.limit ?? 10,
            ...(options?.fields ? { fields: options.fields.join(',') } : {}),
          },
        })
        .then((r) => r.data),
    enabled,
    // Avoid flickering empty states while the user types.
    placeholderData: (previous) => previous,
  })
}
